import React, { useState, useEffect, useMemo, useCallback } from "react";
import { BarChart3, RefreshCw } from "lucide-react";
import {
  fetchUsageSummary,
  fetchUsageByUser,
} from "../../services/usageTrackingService";
import "./AppUsageReport.css";

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const KINDS = [
  { value: "all", label: "All" },
  { value: "page", label: "Pages" },
  { value: "modal", label: "Modals" },
  { value: "action", label: "Actions" },
];

const SUMMARY_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "kind", label: "Type" },
  { key: "opens", label: "Opens", numeric: true },
  { key: "unique_users", label: "People", numeric: true },
  { key: "total_ms", label: "Active time", numeric: true },
  { key: "avg_ms", label: "Avg", numeric: true },
  { key: "last_used", label: "Last used" },
];

const formatDuration = (ms) => {
  if (!ms) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

// Supabase error codes for "function doesn't exist" (migration not run yet)
const isMissingFunction = (error) =>
  ["42883", "PGRST202"].includes(error?.code);

/**
 * AppUsageReport
 * Admin view of which pages and modals get used, by whom, and for how long.
 * Data comes from app_usage_events (see usageTrackingService).
 */
const AppUsageReport = () => {
  const [days, setDays] = useState(30);
  const [kind, setKind] = useState("all");
  const [userEmail, setUserEmail] = useState(null);
  const [summary, setSummary] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: "opens", direction: "desc" });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const since = new Date(Date.now() - days * 86400000).toISOString();
    const [summaryRes, peopleRes] = await Promise.all([
      fetchUsageSummary(since, userEmail),
      fetchUsageByUser(since),
    ]);
    const failed = summaryRes.error || peopleRes.error;
    if (failed) {
      setError(
        isMissingFunction(failed)
          ? "Usage tracking isn't set up in the database yet (app_usage_events migration)."
          : failed.message,
      );
    }
    setSummary(summaryRes.data || []);
    setPeople(peopleRes.data || []);
    setLoading(false);
  }, [days, userEmail]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const filtered = (
      kind === "all" ? summary : summary.filter((r) => r.kind === kind)
    ).map((r) => ({
      ...r,
      // Per open, not per duration row — one view can bank time in several rows
      avg_ms: r.opens ? Math.round(r.total_ms / r.opens) : null,
    }));
    const dir = sort.direction === "asc" ? 1 : -1;
    return filtered.sort((a, b) => {
      const av = a[sort.key] ?? "";
      const bv = b[sort.key] ?? "";
      return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
    });
  }, [summary, kind, sort]);

  const toggleSort = (key) =>
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));

  return (
    <section className="app-usage">
      <div className="app-usage-header">
        <div className="app-usage-title">
          <BarChart3 size={22} />
          <div>
            <h2>App Usage</h2>
            <p>
              Pages and modals opened, and active time spent (idle and hidden
              tabs excluded)
            </p>
          </div>
        </div>
        <button className="app-usage-refresh" onClick={load} disabled={loading}>
          <RefreshCw size={16} className={loading ? "spinning" : ""} />
          Refresh
        </button>
      </div>

      <div className="app-usage-controls">
        <div className="app-usage-segmented">
          {RANGES.map((r) => (
            <button
              key={r.days}
              className={days === r.days ? "active" : ""}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="app-usage-segmented">
          {KINDS.map((k) => (
            <button
              key={k.value}
              className={kind === k.value ? "active" : ""}
              onClick={() => setKind(k.value)}
            >
              {k.label}
            </button>
          ))}
        </div>
        <select
          className="app-usage-select"
          value={userEmail || ""}
          onChange={(e) => setUserEmail(e.target.value || null)}
        >
          <option value="">Everyone</option>
          {people.map((p) => (
            <option key={p.user_email} value={p.user_email}>
              {p.user_email}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="app-usage-error">{error}</div>}

      <div className="app-usage-grid">
        <div className="app-usage-table-wrap">
          <table className="app-usage-table">
            <thead>
              <tr>
                {SUMMARY_COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className={col.numeric ? "numeric" : ""}
                    onClick={() => toggleSort(col.key)}
                  >
                    {col.label}
                    {sort.key === col.key && (
                      <span className="app-usage-sort">
                        {sort.direction === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.kind}:${r.name}`}>
                  <td className="app-usage-name">{r.name}</td>
                  <td>
                    <span className={`app-usage-kind ${r.kind}`}>{r.kind}</span>
                  </td>
                  <td className="numeric">{r.opens}</td>
                  <td className="numeric">{r.unique_users}</td>
                  <td className="numeric">{formatDuration(r.total_ms)}</td>
                  <td className="numeric">{formatDuration(r.avg_ms)}</td>
                  <td>{formatDate(r.last_used)}</td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr>
                  <td
                    colSpan={SUMMARY_COLUMNS.length}
                    className="app-usage-empty"
                  >
                    No usage recorded for this range yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="app-usage-table-wrap">
          <table className="app-usage-table">
            <thead>
              <tr>
                <th>Person</th>
                <th className="numeric">Pages</th>
                <th className="numeric">Modals</th>
                <th className="numeric">Active</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr
                  key={p.user_email}
                  className={p.user_email === userEmail ? "selected" : ""}
                  onClick={() =>
                    setUserEmail(
                      p.user_email === userEmail ? null : p.user_email,
                    )
                  }
                >
                  <td className="app-usage-name">{p.user_email}</td>
                  <td className="numeric">{p.page_views}</td>
                  <td className="numeric">{p.modal_opens}</td>
                  <td className="numeric">{formatDuration(p.active_ms)}</td>
                  <td>{formatDate(p.last_seen)}</td>
                </tr>
              ))}
              {!loading && !people.length && (
                <tr>
                  <td colSpan={5} className="app-usage-empty">
                    Nobody yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default AppUsageReport;
